import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { uploadAttachment } from "@/lib/media";

export const VoiceRecorder = ({ cid, disabled, onSent, onRecording }) => {
  const rec = useRef(null);
  const chunks = useRef([]);
  const cancelled = useRef(false);
  const [secs, setSecs] = useState(null);

  useEffect(() => {
    if (secs === null) return;
    const t = setTimeout(() => setSecs(secs + 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);
  useEffect(() => { onRecording?.(secs !== null); }, [secs !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm"].find((t) => MediaRecorder.isTypeSupported(t));
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = []; cancelled.current = false;
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setSecs(null);
        if (cancelled.current || !chunks.current.length) return;
        const mime = (r.mimeType || "audio/webm").split(";")[0];
        const file = new File(chunks.current, `nota-vocale.${mime.includes("ogg") ? "ogg" : "webm"}`, { type: mime });
        try {
          const { data } = await uploadAttachment(cid, file);
          if (data.delivery === "failed") toast.error(`Invio WhatsApp fallito: ${data.error}`);
          onSent();
        } catch (e) { toast.error(errMsg(e)); }
      };
      r.start();
      rec.current = r;
      setSecs(0);
    } catch {
      toast.error("Impossibile accedere al microfono. Controlla i permessi del browser.");
    }
  };
  const stop = (cancel) => { cancelled.current = cancel; rec.current?.stop(); };

  if (secs !== null) {
    return (
      <div data-testid="voice-recording-indicator" className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-full pl-3 pr-1 h-11">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-mono text-red-700 w-10">{`${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`}</span>
        <button data-testid="voice-cancel-button" onClick={() => stop(true)} title="Annulla" className="w-9 h-9 rounded-full grid place-items-center text-slate-500 hover:bg-red-100"><Trash2 size={16} /></button>
        <button data-testid="voice-stop-send-button" onClick={() => stop(false)} title="Invia nota vocale" className="w-9 h-9 rounded-full grid place-items-center bg-red-500 text-white hover:bg-red-600"><Square size={14} fill="currentColor" /></button>
      </div>
    );
  }
  return (
    <button data-testid="voice-record-button" type="button" disabled={disabled} onClick={start} title="Registra nota vocale"
      className="h-11 w-11 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors disabled:opacity-40">
      <Mic size={18} />
    </button>
  );
};
